import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Skeleton,
  Button,
  Drawer,
  IconButton,
  Slider,
  FormControlLabel,
  Checkbox,
  Divider,
  Select,
  MenuItem,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useCart } from "../../context/CartContext";
import VendorSponsorshipCard from "./VendorSponsorshipCard";
import Popover from "@mui/material/Popover";
import LinearProgress from "@mui/material/LinearProgress";
import PolicyBadges from "./PolicyBadges";
import { getPolicyBadges } from "../../utils/policyBadges";
import SlickSlider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
const VendorStore = () => {
  const { addToCart } = useCart();
  const { vendorId } = useParams();
  const navigate = useNavigate();

  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);
  const [tab, setTab] = useState("products");
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Filter & Sort States
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedRating, setSelectedRating] = useState(0);
  const [sortBy, setSortBy] = useState("relevance");

const [anchorEl, setAnchorEl] = useState(null);
const [open, setOpen] = useState(false);

const handleMouseEnter = (event) => {
  setAnchorEl(event.currentTarget);
  setOpen(true);
};

const handleMouseLeave = () => {
  setOpen(false);
};
  useEffect(() => {
    api
      .get(`/vendor/${vendorId}/store`)
      .then((res) => {
        setVendor(res.data.vendor);
        setProducts(res.data.products);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [vendorId]);

  // Carousel logic for store gallery
  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === (vendor?.storefrontImages?.length || 0) - 1 ? 0 : prevIndex + 1
    );
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? (vendor?.storefrontImages?.length || 0) - 1 : prevIndex - 1
    );
  };
  useEffect(() => {
    if (vendor?.storefrontImages?.length > 1) {
      const interval = setInterval(handleNextImage, 5000); // Change image every 5 seconds
      return () => clearInterval(interval);
    }
  }, [vendor?.storefrontImages?.length, handleNextImage]);

  const refreshVendor = async () => {
    const res = await api.get(`/vendor/${vendorId}/store`);
    setVendor(res.data.vendor);
  };
const about = vendor?.store?.about || {};
  // Extract unique categories from products
  const categories = useMemo(() => {
    const cats = new Set();
    products.forEach((p) => {
      if (p.category) cats.add(p.category);
    });
    return Array.from(cats).sort();
  }, [products]);

  // Extract price range from products
  const priceStats = useMemo(() => {
    if (products.length === 0) return { min: 0, max: 100000 };
    const prices = products.map((p) => p.price);
    return {
      min: Math.floor(Math.min(...prices)),
      max: Math.ceil(Math.max(...prices)),
    };
  }, [products]);

  // Filter & Sort Logic
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = products.filter((p) => {
      // Price filter
      if (p.price < priceRange[0] || p.price > priceRange[1])
        return false;

      // Category filter
      if (
        selectedCategories.length > 0 &&
        !selectedCategories.includes(p.category)
      )
        return false;

      // Rating filter
      if (selectedRating > 0) {
        const productRating = p.rating?.avg || 0;
        if (productRating < selectedRating) return false;
      }

      return true;
    });

    // Sort
    const sorted = [...filtered];
    switch (sortBy) {
      case "price_low":
        sorted.sort((a, b) => a.price - b.price);
        break;
      case "price_high":
        sorted.sort((a, b) => b.price - a.price);
        break;
      case "rating":
        sorted.sort(
          (a, b) => (b.rating?.avg || 0) - (a.rating?.avg || 0)
        );
        break;
      case "newest":
        sorted.sort(
          (a, b) =>
            new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        );
        break;
      case "popularity":
        sorted.sort(
          (a, b) => (b.reviewCount || 0) - (a.reviewCount || 0)
        );
        break;
      default: // relevance
        break;
    }

    return sorted;
  }, [products, priceRange, selectedCategories, selectedRating, sortBy]);

  // Reset filters on tab change
  useEffect(() => {
    setPriceRange([priceStats.min, priceStats.max]);
    setSelectedCategories([]);
    setSelectedRating(0);
    setSortBy("relevance");
  }, [tab, priceStats]);

  if (loading) return <Skeleton variant="rectangular" height={300} />;
  if (!vendor) return <Typography>Store not found</Typography>;

  /* ================= FILTER SIDEBAR ================= */
  const FilterSidebar = () => (
    <Paper
      elevation={1}
      sx={{
        width: "100%",
        maxWidth: 280,
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          Filters
        </Typography>
        <IconButton
          size="small"
          onClick={() => setFilterDrawerOpen(false)}
          sx={{ display: { xs: "block", md: "none" } }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* PRICE FILTER */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
          Price
        </Typography>
        <Slider
          value={priceRange}
          onChange={(e, newValue) => setPriceRange(newValue)}
          min={priceStats.min}
          max={priceStats.max}
          step={100}
          valueLabelDisplay="auto"
          marks={[
            { value: priceStats.min, label: `₹${Number(priceStats.min).toLocaleString("en-IN")}` },
            { value: priceStats.max, label: `₹${Number(priceStats.max).toLocaleString("en-IN")}` },
          ]}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, fontSize: 12, color: "text.secondary" }}>
          <span>₹{Number(priceRange[0]).toLocaleString("en-IN")}</span>
          <span>₹{Number(priceRange[1]).toLocaleString("en-IN")}</span>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* CATEGORY FILTER */}
      {categories.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            Category
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {categories.map((cat) => (
              <FormControlLabel
                key={cat}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedCategories.includes(cat)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCategories([...selectedCategories, cat]);
                      } else {
                        setSelectedCategories(
                          selectedCategories.filter((c) => c !== cat)
                        );
                      }
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: 13 }}>{cat}</Typography>}
              />
            ))}
          </Box>
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* RATING FILTER */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          Rating
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {[4, 3, 2, 1].map((rating) => (
            <FormControlLabel
              key={rating}
              control={
                <Checkbox
                  size="small"
                  checked={selectedRating === rating}
                  onChange={(e) => {
                    setSelectedRating(e.target.checked ? rating : 0);
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: 13 }}>
                  ⭐ {rating}+ Star{rating !== 1 ? "s" : ""}
                </Typography>
              }
            />
          ))}
        </Box>
      </Box>

      {/* CLEAR FILTERS */}
      <Button
        fullWidth
        variant="outlined"
        sx={{
          mt: 2,
          borderColor: "#ff9900",
          color: "#ff9900",
          fontWeight: 600,
          textTransform: "none",
          "&:hover": {
            bgcolor: "#fff3e0",
            borderColor: "#ff9900",
          },
        }}
        onClick={() => {
          setPriceRange([priceStats.min, priceStats.max]);
          setSelectedCategories([]);
          setSelectedRating(0);
          setSortBy("relevance");
        }}
      >
        Clear All Filters
      </Button>
    </Paper>
  );

  const handleAddToCart = (e, product) => {
    e.stopPropagation();

    addToCart({
      id: product._id,
      title: product.title,
      price: product.price,
      imageUrl: product.images?.[0],
      qty: 1,
      vendorId: product.vendorId,
      vendorName: product.vendorName,
    });
  };

  /* ================= PRODUCT CARD ================= */
  const ProductCard = (p) => {
    const stock = p?.stock ?? 0;
    const inStock = stock > 0;
     console.log("STOCK", p);
const badges = getPolicyBadges(p.policies);
    const maxTitleLength = 40;
    const truncatedTitle = p.title
      ? p.title.length > maxTitleLength
        ? p.title.slice(0, maxTitleLength).trimEnd() + "..."
        : p.title
      : "";

    return (
      <Card
        sx={{
          width: '300px',
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          height: "300px",
          // borderColor: hoveredId === p._id ? "#ff9900" : "#ddd",
          // borderWidth: 1,
          // borderStyle: "solid",
          // transition: "0.3s",
          // minHeight: 360,
          // maxHeight: 360,
          boxSizing: "border-box",
          "&:hover": {
            boxShadow: 4,
            transform: "translateY(-2px)",
          },
        }}
        onClick={() => navigate(`/product/${p._id}`)}
        onMouseEnter={() => setHoveredId(p._id)}
        onMouseLeave={() => setHoveredId(null)}
      >
        {/* IMAGE */}
<Box
  sx={{
    height: 180,
    // width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: "#fff",
    p: 1,
    position: "relative",
    overflow: "hidden", // ✅ prevents spill
  }}
>
  <img
    src={
      hoveredId === p._id && p.images?.[1]
        ? p.images[1]
        : p.images?.[0] || "/placeholder-product.png"
    }
    alt=""
    style={{
      maxHeight: "100%",
      maxWidth: "100%",
      objectFit: "contain",
    }}
  />
<PolicyBadges badges={badges} />
</Box>

        {/* OUT OF STOCK */}
        {!inStock && (
          <Box
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              bgcolor: "#fdecea",
              color: "#b71c1c",
              px: 1,
              borderRadius: 1,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Out of Stock
          </Box>
        )}

        {/* CONTENT */}
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            flexGrow: 1,
            p: 0.75,
            // minHeight: 80,
            // maxHeight: 80,
            overflow: "hidden",
          }}
        >
          <Tooltip title={p.title || ""} arrow placement="top">
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.2,
                height: 34,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                textOverflow: "ellipsis",
                wordBreak: "break-word",
                whiteSpace: "normal",
                mb: 0.25,
              }}
            >
              {truncatedTitle}
            </Typography>
          </Tooltip>
          <Typography
            fontWeight={700}
            color="#B12704"
            sx={{ mb: 0.5 }}
          >
            ₹{Number(p.price || 0).toLocaleString("en-IN")}
          </Typography>

          {inStock && (
            <Button
              size="small"
              variant="contained"
              fullWidth
              sx={{
                bgcolor: "#ff9900",
                color: "#000",
                fontWeight: 600,
                textTransform: "none",
                p: "6px 8px",
                minHeight: 30,
                fontSize: 12,
                "&:hover": { bgcolor: "#ff8800" },
              }}
              onClick={(e) => handleAddToCart(e, p)}
            >
              Add to Cart
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ bgcolor: "#eaeded", minHeight: "100vh" }}>
      
      {/* ================= BANNER ================= */}
      <Box
        sx={{
          // mt:"1px",
          height: 220,
          backgroundImage: `url(${vendor.banner || "/default-banner.jpg"})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* ================= HEADER ================= */}
      <Paper
        sx={{
          maxWidth: 1200,
          mx: "auto",
          p: 3,
          mt: -6,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "flex-start", md: "center" }, gap: 2 }}>
          <img
            src={vendor.logo || "/placeholder.png"}
            alt=""
            style={{ width: 80, height: 80, borderRadius: 8 }}
          />

<Box sx={{ flex: 1 }}>

  {/* 🏷 NAME + BADGES */}
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Typography variant="h5" fontWeight={700}>
      {vendor.store?.businessName || vendor.name}
    </Typography>

    {/* ✔️ VERIFIED BADGE */}
    {vendor.isVerified && (
      <Box
        sx={{
          bgcolor: "#e6f4ea",
          color: "#1e8e3e",
          px: 1,
          borderRadius: 1,
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        ✔ Verified
      </Box>
    )}

    {/* 📢 SPONSORED BADGE */}
<VendorSponsorshipCard
  vendorId={vendor._id}
  sponsorshipStatus={vendor.sponsorshipStatus}  
  onApplied={refreshVendor}
/>
  </Box>

  {/* 📝 TAGLINE */}
  <Typography color="text.secondary">
    {vendor.tagline || "Trusted seller"}
  </Typography>

  {/* ⭐ RATING */}

<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
  <Box
    onClick={handleMouseEnter}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      cursor: "pointer",
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    }}
  >
    <Typography sx={{ fontWeight: 600 }}>
      ⭐ {vendor.rating?.avg?.toFixed(1) || "0.0"}
    </Typography>
    <KeyboardArrowDownIcon
      sx={{
        fontSize: 18,
        transition: "transform 0.2s",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
      }}
    />
  </Box>

  <Typography
    variant="body2"
    color="text.secondary"
    onClick={() => navigate(`/vendor/${vendor._id}/reviews`)}
    sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
  >
    ({vendor.rating?.totalReviews || 0} reviews)
  </Typography>
</Box>

<Popover
  open={open}
  anchorEl={anchorEl}
  onClose={() => setOpen(false)}
  disableRestoreFocus
  disableAutoFocus
  disableEnforceFocus
  anchorOrigin={{
    vertical: "bottom",
    horizontal: "left",
  }}
  TransitionProps={{
    timeout: { enter: 0, exit: 0 }
  }}
  PaperProps={{
    sx: {
      p: 2,
      width: 250,
      borderRadius: 2,
    }
  }}
>
  <Typography fontWeight={700} sx={{ mb: 1 }}>
    {vendor.rating?.avg?.toFixed(1)} out of 5
  </Typography>

  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
    {vendor.rating?.totalReviews} global ratings
  </Typography>

  {[5,4,3,2,1].map((star) => (
    <Box key={star} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      
      <Typography sx={{ width: 30, fontSize: 12 }}>
        {star}★
      </Typography>

      <LinearProgress
        variant="determinate"
        value={vendor.rating?.distribution?.[star] || 0}
        sx={{
          flex: 1,
          height: 8,
          borderRadius: 5,
          mx: 1
        }}
      />

      <Typography sx={{ fontSize: 12 }}>
        {vendor.rating?.distribution?.[star] || 0}%
      </Typography>

    </Box>
  ))}
</Popover>

  {/* 📧 CONTACT */}
  <Typography variant="body2" sx={{ mt: 0.5 }}>
    Contact: {vendor.email}
  </Typography>
</Box>

{/* ❤️ FOLLOW BUTTON */}
<Button
  variant="contained"
  sx={{
    bgcolor: "#ff9900",
    color: "#000",
    fontWeight: 600,
    "&:hover": { bgcolor: "#e68a00" },
  }}
>
  Follow
</Button>
        </Box>
      </Paper>

      {/* ================= TABS ================= */}
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          mt: 2,
          display: "flex",
          gap: 3,
          px: 2,
          borderBottom: "1px solid #ddd",
        }}
      >
        {["home", "products", "store policy", "about"].map((t) => (
          <Typography
            key={t}
            onClick={() => setTab(t)}
            sx={{
              cursor: "pointer",
              pb: 1,
              borderBottom:
                tab === t ? "2px solid #000" : "none",
              fontWeight: tab === t ? 600 : 400,
            }}
          >
            {t.toUpperCase()}
          </Typography>
        ))}
      </Box>

      {/* ================= HOME ================= */}
      {tab === "home" && (
        <Box sx={{ maxWidth: 1200, mx: "auto", mt: 3, px: 2, pb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Featured Products
          </Typography>

<Grid container spacing={2}>
  {products.map((p) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={p._id}>
      <ProductCard {...p} />
    </Grid>
  ))}
</Grid>
        </Box>
      )}

      {/* ================= PRODUCTS (WITH FILTERS) ================= */}
      {tab === "products" && (
        <Box sx={{ maxWidth: 1400, mx: "auto", mt: 3, px: 2, pb: 3 }}>
          {/* TOOLBAR: Filter Button + Sort Dropdown */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              mb: 3,
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <IconButton
                onClick={() => setFilterDrawerOpen(true)}
                sx={{
                  display: { xs: "flex", md: "none" },
                  borderRadius: 1,
                  bgcolor: "#f5f5f5",
                }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="body2" color="text.secondary">
                {filteredAndSortedProducts.length} products
              </Typography>
            </Box>

            {/* SORT DROPDOWN */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: 1,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                Sort by:
              </Typography>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                size="small"
                sx={{
                  width: { xs: "100%", sm: 200 },
                  bgcolor: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: 1,
                }}
              >
                <MenuItem value="relevance">Relevance</MenuItem>
                <MenuItem value="price_low">Price: Low to High</MenuItem>
                <MenuItem value="price_high">Price: High to Low</MenuItem>
                <MenuItem value="rating">Highest Rated</MenuItem>
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="popularity">Most Popular</MenuItem>
              </Select>
            </Box>
          </Box>

          {/* MAIN CONTENT: Sidebar + Products */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                flex: "0 0 320px",
                position: "sticky",
                top: 110,
                alignSelf: "flex-start",
              }}
            >
              <FilterSidebar />
            </Box>

            <Box sx={{ flex: 1 }}>
              {filteredAndSortedProducts.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8,
                  }}
                >
                  <Typography variant="h6" color="text.secondary">
                    No products match your filters
                  </Typography>
                  <Button
                    sx={{ mt: 2, color: "#ff9900" }}
                    onClick={() => {
                      setPriceRange([priceStats.min, priceStats.max]);
                      setSelectedCategories([]);
                      setSelectedRating(0);
                      setSortBy("relevance");
                    }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {filteredAndSortedProducts.map((p) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={p._id}  sx={{ display: "flex" }}>
                      {ProductCard(p)}
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* FILTER DRAWER (Mobile) */}
      <Drawer
        anchor="left"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      >
        <Box sx={{ width: 300, p: 2 }}>
          <FilterSidebar />
        </Box>
      </Drawer>
{/* ================= STORE POLICY ================= */}
      {tab === "store policy" && (
        <Box sx={{ maxWidth: 900, mx: "auto", mt: 3, px: 2 }}>
          {vendor?.store?.policies ? (
            <Box>
              {vendor.store.policies.returnPolicy && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Return Policy
                  </Typography>
                  <Typography color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                    {vendor.store.policies.returnPolicy}
                  </Typography>
                </Box>
              )}

              {vendor.store.policies.shippingPolicy && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Shipping Policy
                  </Typography>
                  <Typography color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                    {vendor.store.policies.shippingPolicy}
                  </Typography>
                </Box>
              )}

              {vendor.store.policies.warranty && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Warranty
                  </Typography>
                  <Typography color="text.secondary">
                    {vendor.store.policies.warranty}
                  </Typography>
                </Box>
              )}

              {vendor.store.policies.processingTime && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Processing Time
                  </Typography>
                  <Typography color="text.secondary">
                    {vendor.store.policies.processingTime.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Typography>
                </Box>
              )}

              {!vendor.store.policies.returnPolicy && !vendor.store.policies.shippingPolicy && !vendor.store.policies.warranty && !vendor.store.policies.processingTime && (
                <Typography color="text.secondary">
                  No policies available.
                </Typography>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">
              No policies available.
            </Typography>
          )}
        </Box>
      )}
      
      {/* ================= ABOUT ================= */}
      {tab === "about" && (
  <Box sx={{ maxWidth: 900, mx: "auto", mt: 3, px: 2 }}>

    {/* 🏢 ABOUT HEADER */}
    <Typography variant="h5" fontWeight={700} gutterBottom>
      About {vendor.store?.businessName || vendor.name}
    </Typography>

    {/* 📖 DESCRIPTION (SHORT) */}
    {vendor.description && (
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {vendor.description}
      </Typography>
    )}

    {/* 📜 BUSINESS STORY */}
    {about.businessStory && (
      <>
        <Typography variant="h6" gutterBottom>
          Our Story
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {about.businessStory}
        </Typography>
      </>
    )}

    {/* 🎯 MISSION */}
    {about.mission && (
      <>
        <Typography variant="h6" gutterBottom>
          Our Mission
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {about.mission}
        </Typography>
      </>
    )}

    {/* 💎 VALUES */}
    {about.values?.length > 0 && (
      <>
        <Typography variant="h6" gutterBottom>
          Our Values
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
          {about.values.map((v, i) => (
            <Box
              key={i}
              sx={{
                px: 2,
                py: 0.5,
                bgcolor: "#fff3e0",
                borderRadius: 2,
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {v}
            </Box>
          ))}
        </Box>
      </>
    )}

    {/* ⏳ EXPERIENCE */}
    {about.yearsOfExperience && (
      <Typography sx={{ mb: 3 }}>
        <strong>{about.yearsOfExperience}+ years of experience</strong>
      </Typography>
    )}

    {/* 🎓 CERTIFICATIONS */}
    {about.certifications?.length > 0 && (
      <>
        <Typography variant="h6" gutterBottom>
          Certifications
        </Typography>

        <Box sx={{ mb: 3 }}>
          {about.certifications.map((c, i) => (
            <Typography key={i} color="text.secondary">
              • {c.title} ({c.issuedBy} - {c.year})
            </Typography>
          ))}
        </Box>
      </>
    )}

    {/* 👥 TEAM */}
    {about.team?.length > 0 && (
      <>
        <Typography variant="h6" gutterBottom>
          Meet Our Team
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {about.team.map((member, i) => (
            <Grid item xs={6} md={4} key={i}>
              <Box textAlign="center">
                <img
                  src={member.photo || "/placeholder.png"}
                  alt={member.name}
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    objectFit: "cover",
                    marginBottom: 8,
                  }}
                />
                <Typography fontWeight={600}>
                  {member.name}
                </Typography>
                <Typography fontSize={13} color="text.secondary">
                  {member.role}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </>
    )}

    {/* 🖼 STORE GALLERY */}
    {vendor.storefrontImages?.length > 0 && (
      <>
        <Typography variant="h6" gutterBottom>
          Store Gallery
        </Typography>

        <Box
          sx={{
            width: "100%",
            borderRadius: 2,
            overflow: "hidden",
            mb: 3,
            "& .slick-slider": {
              position: "relative",
            },
            "& .slick-slide": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
            "& .slick-slide img": {
              width: "100%",
              height: "400px",
              objectFit: "cover",
              display: "block",
            },
            "& .slick-prev, & .slick-next": {
              zIndex: 1,
            },
            "& .slick-prev:before, & .slick-next:before": {
              color: "#ffffff",
              fontSize: "24px",
              textShadow: "0 0 10px rgba(0,0,0,0.5)",
            },
            "& .slick-dots": {
              bottom: "15px",
            },
            "& .slick-dots li button:before": {
              color: "white",
              fontSize: "10px",
              textShadow: "0 0 5px rgba(0,0,0,0.5)",
            },
            "& .slick-dots li.slick-active button:before": {
              color: "#ff9900",
            },
          }}
        >
          <SlickSlider
            dots={true}
            infinite={true}
            speed={500}
            slidesToShow={1}
            slidesToScroll={1}
            autoplay={true}
            autoplaySpeed={4000}
            pauseOnHover={true}
            arrows={true}
            prevArrow={<CustomPrevArrow />}
            nextArrow={<CustomNextArrow />}
          >
            {vendor.storefrontImages.map((image, index) => (
              <Box key={index} sx={{ outline: "none" }}>
                <img
                  src={image}
                  alt={`Store gallery ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "400px",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </Box>
            ))}
          </SlickSlider>
        </Box>
      </>
    )}

    {/* 📍 ADDRESS */}
    {vendor.address && (
      <>
        <Typography variant="h6" gutterBottom>
          Address
        </Typography>

        <Typography color="text.secondary">
          {vendor.address.addressLine1}, {vendor.address.city},{" "}
          {vendor.address.state} - {vendor.address.pincode}
        </Typography>
      </>
    )}

  </Box>
)}
    </Box>
  );
};

// Custom Previous Arrow Component
const CustomPrevArrow = (props) => {
  const { className, style, onClick } = props;
  return (
    <Box
      onClick={onClick}
      sx={{
        position: "absolute",
        left: "15px",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        bgcolor: "rgba(0, 0, 0, 0.5)",
        color: "white",
        transition: "all 0.3s ease",
        "&:hover": {
          bgcolor: "rgba(0, 0, 0, 0.8)",
          transform: "translateY(-50%) scale(1.1)",
        },
      }}
    >
      <ArrowBackIosIcon sx={{ fontSize: 24 }} />
    </Box>
  );
};

// Custom Next Arrow Component
const CustomNextArrow = (props) => {
  const { className, style, onClick } = props;
  return (
    <Box
      onClick={onClick}
      sx={{
        position: "absolute",
        right: "15px",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        bgcolor: "rgba(0, 0, 0, 0.5)",
        color: "white",
        transition: "all 0.3s ease",
        "&:hover": {
          bgcolor: "rgba(0, 0, 0, 0.8)",
          transform: "translateY(-50%) scale(1.1)",
        },
      }}
    >
      <ArrowForwardIosIcon sx={{ fontSize: 24 }} />
    </Box>
  );
};

export default VendorStore;